import { type Anmerkung, MAX_ZITAT_ZEICHEN, type TextAnker } from '@awf/shared';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { abschnittVon, type Spanne } from './abschnitte.js';
import { alsHtml } from './als-html.js';
import { Block } from './block.js';
import { BlockPille, blockElement, tabstoppsIm, vorigerTabstopp } from './block-pille.js';
import {
  type Block as BlockDaten,
  istDoppelteBeschriftung,
  istKachel,
  type Zerlegung,
} from './bloecke.js';
import { zeichneBereiche } from './hervorhebung.js';
import { mitMarkerChips } from './marker.js';
import { ankerImText, bereichImAbschnitt, echteTextknoten, kuerzeAnker } from './markierung.js';
import { giltFuer, type Stempel } from './text-stand.js';
import stile from './text-flaeche.module.css';
import website from './website-text.module.css';

/** Eine gemeldete Auswahl, gestempelt mit dem Textstand, den die Flaeche beim Messen zeigte. */
export interface Auswahl extends Stempel {
  bloecke: number[];
  anker: TextAnker | 'grenze' | null;
  /** Relativ zur Textflaeche; null, wenn der Browser keine Masse liefert (jsdom). */
  position: { oben: number; links: number } | null;
  /**
   * Kopie der gemeldeten DOM-Auswahl. Die Entf-Taste wirkt nur, solange genau diese Auswahl
   * noch sichtbar steht (`auswahlStehtNoch`, I-1): Eine per Tastatur veraenderte oder eine
   * ausserhalb neu gezogene Auswahl meldet die Flaeche nicht.
   */
  bereich: Range;
}

export interface TextFlaecheProps {
  zerlegung: Zerlegung;
  grenzen: readonly Spanne[];
  abschnittIds: readonly (string | undefined)[];
  heroBild?: ReactNode;
  /**
   * Titel fuer den Kopf, wenn der Text keine einleitende H1 hat (Kleinkram P5): das Thema des
   * Auftrags. Kein Block, kein Abschnitt, nur Anzeige.
   */
  heroTitel?: string;
  galerie?: ReactNode;
  /** Nur gueltige Anmerkungen -- die Offsets einer ungueltigen zeigen auf fremden Text. */
  anmerkungen: readonly Anmerkung[];
  offenerBlock: number | null;
  bearbeitbar: boolean;
  markierbar: boolean;
  /** Der Stand des gezeigten Textes (`text-stand.ts`); stempelt Auswahl und Klick. */
  textStand: number;
  /**
   * Der Anker eines offenen Kommentarfelds. Nach einem neuen Textstand misst die Flaeche ihn neu
   * und meldet ihn als Auswahl, solange sein Zitat noch an denselben Offsets steht -- sonst
   * stuende die Leiste an der alten Stelle (Triage "vor Merge", T14).
   */
  offeneMarkierung: TextAnker | null;
  onOeffnen(blockIndex: number): void;
  onEingabe(blockIndex: number, raw: string): void;
  onSchliessen(blockIndex: number, raw: string): void;
  onAbbrechen(blockIndex: number): void;
  onAuswahl(auswahl: Auswahl | null): void;
  onAnmerkungGetroffen(anmerkung: Anmerkung): void;
  /**
   * Kommentieren ueber die Leiste am Block (`block-pille.tsx`): die Auswahl ueber den ganzen
   * Block, gemessen erst NACH dem Schliessen eines offenen Blocks -- sonst zeigten die Offsets
   * auf getippten Text, den die Segmente noch nicht kennen. Ohne Handler kein Symbol.
   */
  onBlockKommentieren?(auswahl: Auswahl): void;
  /** Entfernen ueber die Leiste am Block, ebenfalls erst nach dem Schliessen eines offenen Blocks. */
  onBlockEntfernen?(blockIndex: number): void;
  /**
   * Steht gerade die Leiste an einer Markierung (oder deren Kommentarfeld)? Dann gibt es keine
   * Leiste am Block, damit nie zwei gleichzeitig stehen.
   */
  auswahlLeisteSichtbar?: boolean;
}

/**
 * Je Rohtext dasselbe `__html`-Objekt ueber alle Renders: react-dom 19 vergleicht
 * `dangerouslySetInnerHTML` per Identitaet und setzt `innerHTML` sonst neu -- das tauschte
 * die Textknoten aus, und Hervorhebungen und ein offener Block verloren ihren Halt
 * (Learnings 2026-09-23).
 *
 * Die Map waechst sonst unbegrenzt ueber die Lebensdauer der Komponente (Review-Befund
 * Task 9): vor jedem Render auf die aktuell gerenderten Rohtexte begrenzen. Ein bereits
 * gerendertes, unveraendertes `raw` bleibt dabei mit derselben Objektidentitaet erhalten.
 */
// Exportiert nur fuer den eigenen Test (Beschneidung der Ablage).
export function useHtmlJeRaw(rohtexte: readonly string[]): (raw: string) => { __html: string } {
  const ablage = useRef(new Map<string, { __html: string }>());
  const aktuelle = new Set(rohtexte);
  for (const raw of ablage.current.keys()) {
    if (!aktuelle.has(raw)) ablage.current.delete(raw);
  }
  return (raw) => {
    let html = ablage.current.get(raw);
    if (!html) {
      // Chips fuer Inline-Marker (Spec 2026-10-12 §5.5) NACH der Bereinigung, siehe `marker.ts`.
      // Auch fuer den offenen Block: Dasselbe HTML-Objekt haelt die Textknoten, ein Wechsel beim
      // Oeffnen setzte `innerHTML` neu (Learnings 2026-09-23). Die Optik schaltet das CSS ab.
      html = { __html: mitMarkerChips(alsHtml(raw)) };
      ablage.current.set(raw, html);
    }
    return html;
  };
}

function istHero(block: BlockDaten | undefined): block is BlockDaten {
  // Nur eine H1 ganz am Anfang wird Hero -- so bleibt die Dokumentreihenfolge im DOM
  // dieselbe wie im Text, und damit die Zaehlung der Anker (`abschnittsTexte`).
  return (
    block !== undefined &&
    block.index === 0 &&
    block.art === 'ueberschrift' &&
    block.ebene === 1 &&
    block.baustein === null
  );
}

// Wie bisher in RatgeberText: Ein Mausvorgang, der auf einem dieser Elemente BEGINNT, ist keine
// Markierung -- sonst meldete jeder Klick auf "Übernehmen" dieselbe Auswahl erneut. Der offene
// Block zaehlt nicht mehr dazu (Nachbesserung nach Live-Test 2026-11-03, Karte
// j3gkZH2Hgc6K57Oa8f1F): Markieren darin zeigt dieselbe Leiste wie im geschlossenen Block. Ein Link
// darin ist dort Text, kein Bedienelement.
const BEDIENELEMENTE = 'button, textarea, input, select, a, label, [role="button"]';
const OFFENER_BLOCK = '[data-block-index][contenteditable="true"]';
const ABSTAND_UNTER_AUSWAHL_PX = 8;
/**
 * H2 (Codex-Runde 2): Eine Auswahl, die sich ohne Mausvorgang aendert (Umschalt+Pfeil,
 * Touch-Griffe), meldet die Flaeche nach dieser Pause neu. Entprellt, weil jede Taste ein eigenes
 * `selectionchange` ausloest; so kurz, dass die Leiste nachzieht, bevor jemand zu ihr greift.
 */
export const AUSWAHL_TASTATUR_MS = 200;

function startetAufBedienelement(ziel: EventTarget | null): boolean {
  if (!(ziel instanceof Element) || ziel.closest(OFFENER_BLOCK)) return false;
  return ziel.closest(BEDIENELEMENTE) !== null;
}

/**
 * Bloecke, in denen die Auswahl echten Text trifft -- nicht nur einen Randpunkt. D8
 * (preflight-b2.md): baut auf `echteTextknoten` aus `markierung.ts` statt einer eigenen
 * dritten Kopie der Schleife.
 */
function beruehrteBloecke(wurzel: Element, bereich: Range): number[] {
  const indizes = new Set<number>();
  for (const knoten of echteTextknoten(wurzel, bereich)) {
    const wert = knoten.parentElement
      ?.closest('[data-block-index]')
      ?.getAttribute('data-block-index');
    if (wert !== undefined && wert !== null) indizes.add(Number(wert));
  }
  return [...indizes].sort((a, b) => a - b);
}

/**
 * Steht die gemeldete Auswahl noch genau so im Dokument? Nur dann darf die Entf-Taste sie
 * entfernen (I-1): Sonst traefe sie Bloecke, die nicht mehr sichtbar ausgewaehlt sind.
 */
export function auswahlStehtNoch(bereich: Range): boolean {
  const auswahl = window.getSelection();
  if (!auswahl || auswahl.rangeCount === 0 || auswahl.isCollapsed) return false;
  return gleicherBereich(auswahl.getRangeAt(0), bereich);
}

function gleicherBereich(a: Range, b: Range): boolean {
  try {
    return (
      a.compareBoundaryPoints(Range.START_TO_START, b) === 0 &&
      a.compareBoundaryPoints(Range.END_TO_END, b) === 0
    );
  } catch {
    // Verschiedene Wurzeln (der gemeldete Text ist inzwischen ausgetauscht): nicht dieselbe.
    return false;
  }
}

/** Die DOM-Auswahl als Kopie, oder null ohne Auswahl. */
function auswahlJetzt(): Range | null {
  const auswahl = window.getSelection();
  return auswahl && auswahl.rangeCount > 0 ? auswahl.getRangeAt(0).cloneRange() : null;
}

/** Steht am Index noch derselbe Block wie in der Zerlegung, in der der Klick begann (M4)? */
function gleicherBlock(damals: Zerlegung, jetzt: Zerlegung, blockIndex: number): boolean {
  return (
    damals.bloecke.length === jetzt.bloecke.length &&
    damals.bloecke[blockIndex]?.raw === jetzt.bloecke[blockIndex]?.raw
  );
}

export function positionVon(
  wurzel: Element,
  bereich: Range,
): { oben: number; links: number } | null {
  if (typeof bereich.getBoundingClientRect !== 'function') return null;
  const rechteck = bereich.getBoundingClientRect();
  if (rechteck.width === 0 && rechteck.height === 0) return null;
  const basis = wurzel.getBoundingClientRect();
  return {
    oben: rechteck.bottom - basis.top + ABSTAND_UNTER_AUSWAHL_PX,
    links: Math.max(0, rechteck.left - basis.left),
  };
}

export function TextFlaeche(props: TextFlaecheProps) {
  const { zerlegung, grenzen, abschnittIds, heroBild, heroTitel, galerie } = props;
  const htmlFuer = useHtmlJeRaw(zerlegung.bloecke.map((block) => block.raw));
  const huelle = useRef<HTMLDivElement>(null);
  const hinweisId = useId();
  const idVon = (position: number) => abschnittIds[abschnittVon(grenzen, position)];

  // Die Dokument-Listener unten lesen die Props ueber diese Ref, statt sich bei jedem Render
  // ab- und wieder anzumelden (wie in RatgeberText).
  const aktuell = useRef(props);
  useEffect(() => {
    aktuell.current = props;
  });

  // Escape setzt `innerHTML` des offenen Blocks von Hand zurueck (`block.tsx`): neue Textknoten
  // bei gleichen Anmerkungen und gleicher Zerlegung. Ohne eigenen Anlass liefe der Effekt unten
  // nicht, und die Ranges fielen auf leere Stellen zusammen (D16, im Browser belegt, Task 17).
  const [abbrueche, setAbbrueche] = useState(0);

  // Wo die Leiste am Block steht (Karte j3gkZH2Hgc6K57Oa8f1F), immer nur an EINEM Block: am
  // offenen, sonst an dem, dessen Leiste den Fokus hat, sonst unter dem Zeiger, sonst am
  // fokussierten Block (Tastatur). Der Fokus bleibt dem Block zugeordnet, solange er in dessen
  // Leiste steht -- sonst verschwaende sie beim Tab vom offenen Block auf ihr erstes Symbol: Das
  // Blur schliesst den Block, und die Leiste waere weg, bevor der Fokus ankommt. Aus demselben
  // Grund schlaegt ein Fokus in der Leiste den Zeiger: Sonst nahme ein ruhender Zeiger ueber einem
  // anderen Block ihr das fokussierte Symbol unter der Tastatur weg.
  const [zeigerBlock, setZeigerBlock] = useState<number | null>(null);
  const [fokusBlock, setFokusBlock] = useState<{ blockIndex: number; inLeiste: boolean } | null>(
    null,
  );
  // Ein Druck auf ein Symbol der Leiste, der erst nach dem naechsten Commit wirkt: Ist der Block
  // noch offen, schliesst ihn das Blur (und uebernimmt dabei seinen Text). Erst danach stehen
  // Textknoten und Zerlegung so, wie die Seite sie kennt.
  const [blockWunsch, setBlockWunsch] = useState<{
    art: 'kommentieren' | 'entfernen';
    blockIndex: number;
  } | null>(null);

  // Hervorhebung ueber CSS.highlights, ohne das DOM anzufassen (E3). Neu gezeichnet, wenn sich
  // Anmerkungen oder der Text aendern -- ein neuer Text hat neue Textknoten.
  useEffect(() => {
    const wurzel = huelle.current;
    if (!wurzel) return;
    const bereiche = props.anmerkungen.flatMap((a) => {
      const bereich = bereichImAbschnitt(wurzel, a.anker.abschnittId, a.anker.von, a.anker.bis);
      return bereich && bereich.toString() === a.anker.zitat ? [bereich] : [];
    });
    zeichneBereiche(wurzel, bereiche);
    return () => {
      zeichneBereiche(wurzel, []);
    };
  }, [props.anmerkungen, zerlegung, abbrueche]);

  // Auswahl-Erkennung wie bisher in RatgeberText (mousedown/selectionchange/mouseup am
  // `document`, ein Tick Verzoegerung fuer `selectionchange`), jetzt ueber die ganze Flaeche.
  const aktiv = props.bearbeitbar || props.markierbar;
  useEffect(() => {
    const wurzel = huelle.current;
    if (!wurzel || !aktiv) return;
    let aufBedienelement = false;
    let hatteMausAb = false;
    let geaendert = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let tastaturTimer: ReturnType<typeof setTimeout> | null = null;
    // Die zuletzt entschiedene Auswahl: die, mit der der letzte Mausvorgang endete, oder die
    // zuletzt ohne Maus gemeldete. Ueber sie ist schon entschieden (auch "keine Leiste", etwa bei
    // Beginn auf einem Bedienelement); ein nachlaufendes `selectionchange` mit derselben Auswahl
    // meldet nichts neu.
    let mausAuswahl: Range | null = null;

    const tastaturAbbrechen = () => {
      if (tastaturTimer !== null) clearTimeout(tastaturTimer);
      tastaturTimer = null;
    };
    const mausAb = (ereignis: MouseEvent) => {
      aufBedienelement = startetAufBedienelement(ereignis.target);
      // Ein Druck auf ein Bedienelement (etwa einen Leistenknopf) laesst eine ausstehende
      // Neumeldung stehen: Wirkt der Knopf wegen abweichender Auswahl nicht, holt sie die Leiste
      // fuer die sichtbare Auswahl zurueck. Ein Mausvorgang im Text entscheidet selbst.
      if (!aufBedienelement) tastaturAbbrechen();
      hatteMausAb = true;
      geaendert = false;
    };
    // H2: Ohne Mausvorgang (Tastatur, Touch-Griffe) gibt es kein `mouseup`, das die neue Auswahl
    // meldete -- die Leiste bliebe bei der alten Stelle stehen. Waehrend eines Mausvorgangs
    // entscheidet weiter `mausLos`.
    const auswahlGeaendert = () => {
      geaendert = true;
      if (hatteMausAb) return;
      tastaturAbbrechen();
      tastaturTimer = setTimeout(ohneMaus, AUSWAHL_TASTATUR_MS);
    };
    const ohneMaus = () => {
      // Kleinkram P1: Haelt die Maus gerade einen Knopf gedrueckt, verschoebe eine Meldung jetzt
      // die Leiste unter dem Zeiger, und der Klick ginge ins Leere. Nachgemeldet wird erst, wenn
      // `verarbeite` den Mausvorgang abgeschlossen hat.
      if (hatteMausAb) {
        tastaturTimer = setTimeout(ohneMaus, AUSWAHL_TASTATUR_MS);
        return;
      }
      tastaturTimer = null;
      geaendert = false;
      // Eine Auswahl in einem Eingabefeld ist Tippen, kein Markieren; wie beim Mausvorgang auf
      // einem Bedienelement bleibt es bei der bisherigen Meldung. Der offene Block meldet seine
      // Auswahl dagegen wie der geschlossene (Umschalt+Pfeil markiert dort genauso). Die Leiste
      // nimmt dabei keinen Fokus; Tippen ersetzt die Auswahl, und die naechste Meldung raeumt sie.
      const aktiv = document.activeElement;
      if (aktiv instanceof Element && aktiv.closest('textarea, input, select')) return;
      // Dieselbe Auswahl wie am Ende des letzten Mausvorgangs: schon entschieden (Spec §9, eine
      // Auswahl, die auf einem Bedienelement beginnt, zeigt keine Leiste -- auch nicht spaeter).
      const jetzt = auswahlJetzt();
      if (jetzt && mausAuswahl && gleicherBereich(jetzt, mausAuswahl)) return;
      if (!jetzt && !mausAuswahl) return;
      // Kleinkram P1: Ab jetzt ist DIESE Auswahl entschieden. Bliebe die des Mausvorgangs stehen,
      // meldete eine Rueckkehr der Tastatur auf genau sie nichts, und die Leiste klebte an der
      // Zwischenstelle. Bewusst in Kauf genommen: Kehrt die Tastatur auf eine Auswahl zurueck, die
      // einmal auf einem Bedienelement begann (Spec §9, keine Leiste), zeigt sie jetzt eine. Sie ist
      // inzwischen eine Tastaturauswahl.
      mausAuswahl = jetzt;
      melde();
    };
    const verarbeite = () => {
      timer = null;
      const warAufBedienelement = aufBedienelement;
      const wurdeGeaendert = geaendert;
      const mitMausAb = hatteMausAb;
      aufBedienelement = false;
      hatteMausAb = false;
      geaendert = false;
      // Ein Mausvorgang, der die Auswahl nicht veraendert hat, hat ueber sie auch nichts
      // entschieden (etwa ein Klick auf einen Leistenknopf).
      if (mitMausAb && !wurdeGeaendert) return;
      mausAuswahl = auswahlJetzt();
      if (warAufBedienelement) return;
      melde();
    };
    const melde = () => {
      const auswahl = window.getSelection();
      if (!auswahl || auswahl.rangeCount === 0 || auswahl.isCollapsed) {
        aktuell.current.onAuswahl(null);
        return;
      }
      const bereich = auswahl.getRangeAt(0);
      // Eine neue Auswahl ganz ausserhalb (Seitentitel, Zitat in der Spalte) ersetzt die alte:
      // Bliebe die alte gemeldet, entfernte die Ruecktaste unsichtbar deren Bloecke (I-1). Ein
      // offenes Kommentarfeld schuetzt die Seite selbst (`auswahlGemeldet`).
      const bloecke =
        wurzel.contains(bereich.startContainer) || wurzel.contains(bereich.endContainer)
          ? beruehrteBloecke(wurzel, bereich)
          : [];
      if (bloecke.length === 0) {
        aktuell.current.onAuswahl(null);
        return;
      }
      aktuell.current.onAuswahl({
        stand: aktuell.current.textStand,
        bloecke,
        anker: aktuell.current.markierbar ? ankerImText(wurzel, bereich) : null,
        position: positionVon(wurzel, bereich),
        bereich: bereich.cloneRange(),
      });
    };
    const mausLos = () => {
      if (!hatteMausAb) {
        verarbeite();
        return;
      }
      timer = setTimeout(verarbeite, 0);
    };
    document.addEventListener('mousedown', mausAb);
    document.addEventListener('selectionchange', auswahlGeaendert);
    document.addEventListener('mouseup', mausLos);
    return () => {
      document.removeEventListener('mousedown', mausAb);
      document.removeEventListener('selectionchange', auswahlGeaendert);
      document.removeEventListener('mouseup', mausLos);
      if (timer !== null) clearTimeout(timer);
      tastaturAbbrechen();
    };
  }, [aktiv]);

  // Nach einem neuen Textstand die offene Markierung neu vermessen (siehe `offeneMarkierung`).
  // Nur ihre Position und ihr Stempel werden neu; ist ihr Zitat nicht mehr an denselben Offsets,
  // bleibt sie beim alten Stempel, und die Seite meldet das beim Uebernehmen oder Absenden.
  useEffect(() => {
    const wurzel = huelle.current;
    const { offeneMarkierung, markierbar, textStand } = aktuell.current;
    if (!wurzel || !offeneMarkierung || !markierbar) return;
    const bereich = bereichImAbschnitt(
      wurzel,
      offeneMarkierung.abschnittId,
      offeneMarkierung.von,
      offeneMarkierung.bis,
    );
    if (!bereich || bereich.toString() !== offeneMarkierung.zitat) return;
    const bloecke = beruehrteBloecke(wurzel, bereich);
    if (bloecke.length === 0) return;
    aktuell.current.onAuswahl({
      stand: textStand,
      bloecke,
      anker: ankerImText(wurzel, bereich),
      position: positionVon(wurzel, bereich),
      bereich,
    });
  }, [zerlegung]);

  useEffect(() => {
    if (!blockWunsch) return;
    setBlockWunsch(null);
    const wurzel = huelle.current;
    const { offenerBlock, onBlockKommentieren, onBlockEntfernen, markierbar, textStand } =
      aktuell.current;
    // Blieb der Block offen (das Blur hat ihn nicht geschlossen), wirkt der Druck nicht: Die
    // Offsets gehoerten zu ungesichertem Text.
    if (!wurzel || offenerBlock === blockWunsch.blockIndex) return;
    if (blockWunsch.art === 'entfernen') {
      onBlockEntfernen?.(blockWunsch.blockIndex);
      // Die Leiste gehoerte zum entfernten Block; an seinem Index steht jetzt ein anderer.
      setFokusBlock(null);
      setZeigerBlock(null);
      return;
    }
    const element = blockElement(wurzel, blockWunsch.blockIndex);
    if (!element || !onBlockKommentieren) return;
    const bereich = document.createRange();
    bereich.selectNodeContents(element);
    // Ein langer Block wird am Anfang zitiert, statt gar nicht kommentierbar zu sein.
    const anker = markierbar ? ankerImText(wurzel, bereich) : null;
    onBlockKommentieren({
      stand: textStand,
      bloecke: [blockWunsch.blockIndex],
      anker: anker && anker !== 'grenze' ? kuerzeAnker(anker, MAX_ZITAT_ZEICHEN) : anker,
      position: positionVon(wurzel, bereich),
      bereich,
    });
  }, [blockWunsch]);

  function pillenDruck(art: 'kommentieren' | 'entfernen', blockIndex: number) {
    // Bleibt der Fokus beim Druck im offenen Block (Safari fokussiert Knoepfe beim Klick nicht),
    // schliesst erst dieses Blur ihn. Liegt der Fokus schon in der Leiste, hat der Druck ihn
    // bereits geschlossen.
    const fokus = document.activeElement;
    const block = blockElement(huelle.current, blockIndex);
    if (fokus instanceof HTMLElement && block?.contains(fokus)) fokus.blur();
    setBlockWunsch({ art, blockIndex });
  }

  // Der Stand, auf dem der letzte Druck auf die Flaeche begann (M4). Ein Blur zwischen Druck und
  // Klick kann den Text aendern; der Index des Klicks gehoert dann zur alten Zerlegung. Ersetzt die
  // fruehere Wache `indexVerschoben` mit `setTimeout(0)`, die bei echtem Timing nie griff
  // (Task 17).
  const druck = useRef<{ stand: number; zerlegung: Zerlegung } | null>(null);

  function klick(blockIndex: number) {
    // Jeder Klick verbraucht seinen Druck, auch einer, der gleich unten markiert oder trifft.
    const gedrueckt = druck.current;
    druck.current = null;
    const auswahl = window.getSelection();
    // Eine echte Auswahl ist Markieren, kein Bearbeiten (Spec B-E5).
    if (auswahl && !auswahl.isCollapsed && auswahl.toString().trim() !== '') return;
    const wurzel = huelle.current;
    if (wurzel && auswahl && auswahl.rangeCount > 0) {
      const punkt = auswahl.getRangeAt(0);
      const getroffen = props.anmerkungen.find(
        (a) =>
          bereichImAbschnitt(wurzel, a.anker.abschnittId, a.anker.von, a.anker.bis)?.isPointInRange(
            punkt.startContainer,
            punkt.startOffset,
          ) ?? false,
      );
      if (getroffen) {
        props.onAnmerkungGetroffen(getroffen);
        return;
      }
    }
    // Ohne Druck (Tastatur, synthetischer Klick) gilt der Klick dem gezeigten Stand.
    if (
      gedrueckt &&
      !giltFuer(gedrueckt, props.textStand, () =>
        gleicherBlock(gedrueckt.zerlegung, zerlegung, blockIndex),
      )
    ) {
      return;
    }
    const block = zerlegung.bloecke[blockIndex];
    if (!props.bearbeitbar || !block?.editierbar || props.offenerBlock === blockIndex) return;
    props.onOeffnen(blockIndex);
  }

  const zeichneBlock = (block: BlockDaten) => (
    <Block
      key={block.index}
      block={block}
      kachel={istKachel(block, zerlegung.bausteine)}
      dopplung={istDoppelteBeschriftung(block, zerlegung)}
      html={htmlFuer(block.raw)}
      abschnittId={idVon(block.von)}
      hinweisId={hinweisId}
      bearbeitbar={props.bearbeitbar}
      onKlick={klick}
      offen={props.offenerBlock === block.index}
      onOeffnen={(i) => {
        if (props.bearbeitbar) props.onOeffnen(i);
      }}
      onEingabe={props.onEingabe}
      onSchliessen={props.onSchliessen}
      onAbbrechen={(i) => {
        setAbbrueche((n) => n + 1);
        props.onAbbrechen(i);
      }}
    />
  );

  const blockVon = (ziel: EventTarget | null): number | null => {
    const element = ziel instanceof Element ? ziel.closest('[data-block-index]') : null;
    if (!element || !huelle.current?.contains(element)) return null;
    return Number(element.getAttribute('data-block-index'));
  };
  const leisteVon = (ziel: EventTarget | null): number | null => {
    const wert =
      ziel instanceof Element
        ? ziel.closest('[data-block-pille]')?.getAttribute('data-block-pille')
        : null;
    return wert === null || wert === undefined ? null : Number(wert);
  };
  const inLeiste = (ziel: EventTarget | null) => leisteVon(ziel) !== null;
  const leistenBlock =
    props.offenerBlock ??
    (fokusBlock?.inLeiste ? fokusBlock.blockIndex : null) ??
    zeigerBlock ??
    fokusBlock?.blockIndex ??
    null;
  // Genau eine Leiste: Steht die Leiste an einer Markierung (oder ihr Kommentarfeld), gibt es
  // keine Leiste am Block, auch nicht am offenen (Live-Test 2026-11-03).
  const pillenBloecke =
    aktiv &&
    !props.auswahlLeisteSichtbar &&
    leistenBlock !== null &&
    leistenBlock < zerlegung.bloecke.length
      ? [leistenBlock]
      : [];

  const [erster] = zerlegung.bloecke;
  const hero = istHero(erster) ? erster : undefined;
  const ersatzTitel = hero ? undefined : heroTitel || undefined;
  const rest = hero ? zerlegung.bloecke.slice(1) : zerlegung.bloecke;

  // Aufeinanderfolgende Bloecke desselben Bausteins bilden eine Huelle.
  const gruppen: { baustein: number | null; bloecke: BlockDaten[] }[] = [];
  for (const block of rest) {
    const letzte = gruppen[gruppen.length - 1];
    if (letzte && block.baustein !== null && letzte.baustein === block.baustein) {
      letzte.bloecke.push(block);
    } else {
      gruppen.push({ baustein: block.baustein, bloecke: [block] });
    }
  }

  return (
    <div
      ref={huelle}
      className={`${website.websiteText} ${stile.flaeche}`}
      onPointerDown={() => {
        druck.current = { stand: props.textStand, zerlegung };
      }}
      onPointerOver={(ereignis) => {
        // Erst ein anderer Block nimmt die Leiste mit. Auf ihr selbst und auf dem Weg dorthin
        // (sie schwebt rechts ueber dem Block, ueber freier Flaeche) bleibt sie bei ihrem Block.
        if (inLeiste(ereignis.target)) return;
        const block = blockVon(ereignis.target);
        if (block !== null) setZeigerBlock(block);
      }}
      onPointerLeave={() => setZeigerBlock(null)}
      onFocus={(ereignis) => {
        const leiste = leisteVon(ereignis.target);
        if (leiste !== null) {
          setFokusBlock({ blockIndex: leiste, inLeiste: true });
          return;
        }
        const block = blockVon(ereignis.target);
        setFokusBlock(block === null ? null : { blockIndex: block, inLeiste: false });
      }}
      onBlur={(ereignis) => {
        const nach = ereignis.relatedTarget;
        if (!(nach instanceof Node) || !huelle.current?.contains(nach)) setFokusBlock(null);
      }}
      onKeyDown={(ereignis) => {
        // Die Leiste steht im DOM am Ende der Flaeche (siehe `block-pille.tsx`). Per Tab folgt sie
        // trotzdem direkt auf den Block: erst der Block und seine Links, dann die Leiste, dann der
        // naechste Block. Rueckwaerts genauso.
        if (ereignis.key !== 'Tab' || ereignis.altKey || ereignis.ctrlKey || ereignis.metaKey) {
          return;
        }
        const wurzel = huelle.current;
        const ziel = ereignis.target;
        if (!wurzel || !(ziel instanceof HTMLElement) || inLeiste(ziel)) return;
        const block = ziel.closest<HTMLElement>('[data-block-index]');
        if (!block || !wurzel.contains(block)) return;
        const stopps = tabstoppsIm(block);
        if (!ereignis.shiftKey) {
          // Nur vom LETZTEN Tab-Stopp des Blocks in die Leiste; davor fuehrt der Browser durch
          // die Links im Block (Codex-Review PR #112, HOCH).
          if (ziel !== stopps.at(-1)) return;
          const erstes = wurzel.querySelector(
            `[data-block-pille="${block.getAttribute('data-block-index')}"] button:not(:disabled)`,
          );
          if (erstes instanceof HTMLElement) {
            ereignis.preventDefault();
            erstes.focus();
          }
          return;
        }
        // Rueckwaerts vom ERSTEN Tab-Stopp des Blocks: Gehoert der vorige Tab-Stopp zu einem
        // anderen Block, steht dessen Leiste dazwischen. Sie entsteht erst mit dem Fokus, deshalb
        // synchron rendern, bevor sie den Fokus bekommt. Ein offener Block behaelt die Leiste;
        // dann bleibt es beim Browser.
        if (ziel !== stopps[0] || props.offenerBlock !== null) return;
        const vorher = vorigerTabstopp(wurzel, block);
        const vorigerBlock = vorher?.closest<HTMLElement>('[data-block-index]');
        if (!vorigerBlock || !wurzel.contains(vorigerBlock)) return;
        const index = Number(vorigerBlock.getAttribute('data-block-index'));
        flushSync(() => setFokusBlock({ blockIndex: index, inLeiste: true }));
        const knoepfe = wurzel.querySelectorAll<HTMLElement>(
          `[data-block-pille="${index}"] button:not(:disabled)`,
        );
        const letzter = knoepfe[knoepfe.length - 1];
        if (letzter) {
          ereignis.preventDefault();
          letzter.focus();
        } else {
          // Keine Leiste an diesem Block (nichts anzubieten): Zuordnung zuruecknehmen, der
          // Browser fuehrt wie gewohnt zurueck.
          setFokusBlock({ blockIndex: index, inLeiste: false });
        }
      }}
    >
      {(hero || heroBild || ersatzTitel) && (
        <header className={website.hero} data-hero data-mit-bild={heroBild ? '' : undefined}>
          {/* `heroBild` steht im DOM VOR dem Text (R9-1): Ein Bild mit eigenem sichtbaren
              Text (Bildunterschrift, spaeter Task 12) darf nicht zwischen den Textknoten von
              Abschnitt 0 liegen (DOM-Vertrag Ue Sec2). Die Optik -- Text links, Bild rechts,
              39/56 -- kommt allein aus benannten Grid-Flaechen (`grid-template-areas`) in
              `website-text.module.css`, nicht aus dieser Reihenfolge. `data-mit-bild` schaltet
              dort die Zweispaltigkeit frei: ohne heroBild bleibt der Titel einspaltig (M-b). */}
          {heroBild && <div className={website.heroBild}>{heroBild}</div>}
          {/* Kein leerer .heroText-Wrapper ohne H1 (Task-9-Minor): heroBild ohne einleitende
              H1 rendert sonst ein leeres Element im Grid. */}
          {hero && <div className={website.heroText}>{zeichneBlock(hero)}</div>}
          {/* Ersatztitel ohne einleitende H1 (Kleinkram P5): wie die H1 gesetzt, aber kein Block
              und ausserhalb jedes Abschnitts. `aria-hidden`, denn die Seiten-h1 nennt dasselbe
              Thema schon; vorgelesen wuerde es doppelt. */}
          {ersatzTitel && (
            <div className={website.heroText} aria-hidden="true">
              <h1>{ersatzTitel}</h1>
            </div>
          )}
        </header>
      )}
      <div className={website.inhalt}>
        {gruppen.map((gruppe) => {
          const region =
            gruppe.baustein === null ? undefined : zerlegung.bausteine[gruppe.baustein];
          if (!region) return gruppe.bloecke.map(zeichneBlock);
          const labelId = idVon(region.von);
          return (
            <section
              key={`baustein-${gruppe.baustein}`}
              className={website.baustein}
              data-baustein={region.name}
            >
              <span
                className={website.bausteinName}
                contentEditable={false}
                {...(labelId ? { 'data-abschnitt-id': labelId } : {})}
              >
                {region.name}
              </span>
              {gruppe.bloecke.map(zeichneBlock)}
            </section>
          );
        })}
        {galerie && (
          <div className={website.galerie} data-galerie>
            {galerie}
          </div>
        )}
      </div>
      {pillenBloecke.map((blockIndex) => {
        const block = zerlegung.bloecke[blockIndex];
        return (
          <BlockPille
            key={blockIndex}
            blockIndex={blockIndex}
            wurzel={huelle}
            kommentieren={
              props.markierbar &&
              props.onBlockKommentieren !== undefined &&
              block !== undefined &&
              idVon(block.von) !== undefined
            }
            entfernen={props.bearbeitbar && props.onBlockEntfernen !== undefined}
            onKommentieren={(i) => pillenDruck('kommentieren', i)}
            onEntfernen={(i) => pillenDruck('entfernen', i)}
          />
        );
      })}
      <p id={hinweisId} className={stile.nurVorlesen}>
        Klicken oder Enter drücken, um diesen Block zu bearbeiten.
      </p>
    </div>
  );
}
